import { APP_READY_CLASS, AUTH_LOADER_ENTRY_MS, LOADER_LEFT_AT_STORAGE_KEY, LOADER_START_ATTRIBUTE, LOGIN_CONTINUE_CLASS, LOGIN_LAYOUT_CLASS, LOGIN_STILL_CLASS } from "./bootLoaderScript";
import { BRAND_LOADER_LOOP_MS } from "./brandLoader";

/**
 * A login loader that appears within this time after the previous one went away takes over from it: the same
 * route component tree swaps loaders in one render, and the index.html loader fades out in the render that mounts
 * the page's own one. A longer gap means the user saw another screen in between, so the entry plays again.
 */
export const AUTH_LOADER_HANDOFF_MS = 250;

/** The dot rests in the logo for the first 4% of a round (authLoader.css): leaving then still shows a whole hop. */
const DOT_AT_HOME_MS = BRAND_LOADER_LOOP_MS * 0.04;

export interface AuthLoaderClock {
  /** A login loader appears: returns how long the loader on screen has been running, in ms (0 for a new one). */
  acquire: (now?: number) => number
  /** A login loader went away. */
  release: (now?: number) => void
  /** The app showed (false) or hid (true) the index.html loader (`useBootLoader`). */
  noteBootLoaderHidden: (hidden: boolean, now?: number) => void
  /**
   * How long until the login loader on screen ends a round, in ms: the end of the first round after the entry, or of
   * the round that is playing. 0 when no login loader is on screen.
   */
  msUntilRoundEnd: (now?: number) => number
}

/**
 * When the index.html loader shows the login loader (login layout, not a still form) and is on screen or was hidden
 * just now: the moment its animation started, on the clock of performance.now().
 */
function bootLoaderStart(now: number, hiddenAt: number | null): number | null {
  const root = document.documentElement;
  if (!root.classList.contains(LOGIN_LAYOUT_CLASS) || root.classList.contains(LOGIN_STILL_CLASS)) {
    return null;
  }
  if (root.classList.contains(APP_READY_CLASS) && (hiddenAt === null || now - hiddenAt > AUTH_LOADER_HANDOFF_MS)) {
    return null;
  }

  // A page that continues the previous one started with the entry already done.
  const skipped = root.classList.contains(LOGIN_CONTINUE_CLASS) ? AUTH_LOADER_ENTRY_MS : 0;
  const stage = document.querySelector(".vb-loader-screen .auth-loader__stage");
  const startTime = stage && typeof stage.getAnimations === "function" ? stage.getAnimations()[0]?.startTime : null;
  if (typeof startTime === "number") {
    return startTime - skipped;
  }

  // Without the Web Animations API: the boot script noted when the loader went on screen.
  const noted = root.getAttribute(LOADER_START_ATTRIBUTE);
  if (noted === null || !Number.isFinite(Number(noted))) {
    return null;
  }

  return Number(noted) - skipped;
}

/**
 * The page leaves while a login loader is on screen (the browser goes to the next login step): the next page of
 * this tab then starts the loader with the logo in the middle instead of gliding in again (bootLoaderScript.ts).
 */
export function rememberLoginLoaderOnLeave(now: number = Date.now()): void {
  try {
    window.sessionStorage.setItem(LOADER_LEFT_AT_STORAGE_KEY, String(now));
  } catch {
    // Blocked storage: the next page glides in again.
  }
}

/**
 * One timeline for the login loader: from index.html to the page's own AuthLoader and from one login step to the
 * next, the loader continues where it was instead of starting again from the top of the column.
 */
export function createAuthLoaderClock(): AuthLoaderClock {
  let origin = 0;
  let holders = 0;
  let lastSeenAt = Number.NEGATIVE_INFINITY;
  let bootHiddenAt: number | null = null;

  return {
    acquire(now = performance.now()) {
      if (holders === 0 && now - lastSeenAt > AUTH_LOADER_HANDOFF_MS) {
        origin = bootLoaderStart(now, bootHiddenAt) ?? now;
      }
      holders += 1;

      return Math.max(0, now - origin);
    },
    release(now = performance.now()) {
      holders = Math.max(0, holders - 1);
      if (holders === 0) {
        lastSeenAt = now;
      }
    },
    noteBootLoaderHidden(hidden, now = performance.now()) {
      bootHiddenAt = hidden ? now : null;
    },
    msUntilRoundEnd(now = performance.now()) {
      if (holders === 0) {
        return 0;
      }

      const elapsed = Math.max(0, now - origin);
      const intoRounds = elapsed - AUTH_LOADER_ENTRY_MS;
      if (intoRounds >= BRAND_LOADER_LOOP_MS && intoRounds % BRAND_LOADER_LOOP_MS < DOT_AT_HOME_MS) {
        return 0;
      }
      const rounds = Math.max(1, Math.ceil(intoRounds / BRAND_LOADER_LOOP_MS));

      return AUTH_LOADER_ENTRY_MS + rounds * BRAND_LOADER_LOOP_MS - elapsed;
    },
  };
}

export const authLoaderClock = createAuthLoaderClock();

/**
 * Resolves once the login loader on screen has played its entry and a whole round, so a page that leaves for the next
 * login step never cuts the dot off mid hop. Resolves right away without a login loader or with reduced motion,
 * where the loader stands still.
 */
export function waitForAuthLoaderRound(clock: AuthLoaderClock = authLoaderClock): Promise<void> {
  const reducedMotion = typeof window.matchMedia === "function" && window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  const wait = reducedMotion ? 0 : clock.msUntilRoundEnd();
  if (wait === 0) {
    return Promise.resolve();
  }

  return new Promise(resolve => window.setTimeout(resolve, wait));
}
